import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Clapperboard, Video, Megaphone, CalendarDays, Briefcase, Rocket, Sparkles, Folder, Loader2, Check } from "lucide-react";
import { cn } from "@/lib/utils";

const ICON_MAP = {
  clapperboard: Clapperboard,
  video: Video,
  megaphone: Megaphone,
  "calendar-days": CalendarDays,
  briefcase: Briefcase,
  rocket: Rocket,
  sparkles: Sparkles,
  folder: Folder,
};

/**
 * Two-step template flow:
 *   Step 1 — pick template (grid of built-ins + AI generator + Blank)
 *   Step 2 — configure new project (title, deadline) + checklist of stages/tasks/folders
 *
 * Emits onCreated(project) when the API responds.
 */
export default function TemplatePickerDialog({ open, onOpenChange, onCreated }) {
  const [templates, setTemplates] = useState({ built_in: [], custom: [] });
  const [chosen, setChosen] = useState(null); // ProjectTemplate | { slug: '__blank__' } | { slug: '__ai__' }
  const [step, setStep] = useState("pick"); // pick | configure | ai
  const [form, setForm] = useState({ project_title: "", deadline: "" });
  const [included, setIncluded] = useState({ stages: {}, tasks: {}, folders: {} });
  const [applying, setApplying] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("pick"); setChosen(null); setForm({ project_title: "", deadline: "" });
    api.get("/project-templates").then(({ data }) => setTemplates(data)).catch((err) => console.error("Could not load templates", err));
  }, [open]);

  const openConfigure = (tpl) => {
    setChosen(tpl);
    setForm({ project_title: "", deadline: "" });
    // Pre-tick every stage/task/folder — the checklist is opt-out
    setIncluded({
      stages: Object.fromEntries((tpl.stages || []).map((s) => [s.key, true])),
      tasks: Object.fromEntries((tpl.tasks || []).map((t) => [t.title, true])),
      folders: Object.fromEntries((tpl.folders || []).map((f) => [f.key, true])),
    });
    setStep("configure");
  };

  const runAI = async () => {
    if (!aiBrief.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/project-templates/ai-generate", { brief: aiBrief.trim() });
      openConfigure(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally { setAiBusy(false); }
  };

  const apply = async () => {
    if (!form.project_title.trim()) { toast.error("Give the project a title"); return; }
    setApplying(true);
    try {
      const includedStageKeys = Object.entries(included.stages).filter(([, v]) => v).map(([k]) => k);
      const includedTaskTitles = Object.entries(included.tasks).filter(([, v]) => v).map(([k]) => k);
      const includedFolderKeys = Object.entries(included.folders).filter(([, v]) => v).map(([k]) => k);
      if (chosen.slug === "__blank__") {
        const { data } = await api.post("/projects", {
          title: form.project_title.trim(),
          type: "client job",
          status: "active",
          deadline: form.deadline || null,
        });
        toast.success("Project created");
        onCreated?.({ project: data, tasks_created: 0, folders_created: 0 });
      } else {
        const { data } = await api.post("/projects/from-template", {
          template_slug_or_id: chosen.slug || chosen.id,
          project_title: form.project_title.trim(),
          deadline: form.deadline || null,
          included_stage_keys: includedStageKeys,
          included_task_titles: includedTaskTitles,
          included_folder_keys: includedFolderKeys,
        });
        toast.success(`Project created · ${data.tasks_created} tasks · ${data.folders_created} folders`);
        onCreated?.(data);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create project");
    } finally { setApplying(false); }
  };

  const allTemplates = useMemo(() => [
    ...(templates.built_in || []),
    ...(templates.custom || []),
  ], [templates]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-picker-dialog">
        <DialogHeader>
          <DialogTitle>
            {step === "pick" && "Start a new project"}
            {step === "configure" && `${chosen?.name || "Configure"} — pick what to include`}
            {step === "ai" && "Draft a template with AI"}
          </DialogTitle>
          {step === "pick" && (
            <DialogDescription>
              Pick a template to pre-plan stages, tasks and folders — or start blank.
            </DialogDescription>
          )}
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allTemplates.map((t) => {
                const Icon = ICON_MAP[t.icon] || Folder;
                return (
                  <button
                    key={t.slug || t.id}
                    onClick={() => openConfigure(t)}
                    data-testid={`template-card-${t.slug || t.id}`}
                    className="text-left p-4 rounded-xl border border-border hover:border-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary)/0.03)] transition-colors group"
                  >
                    <Icon className="w-6 h-6 text-[hsl(var(--studio-primary))]" strokeWidth={1.75} />
                    <div className="mt-2 font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>
                    <div className="mt-3 text-[11px] text-muted-foreground">
                      {(t.stages || []).length} stages · {(t.tasks || []).length} tasks · {(t.folders || []).length} folders
                    </div>
                  </button>
                );
              })}
              <button
                onClick={() => setStep("ai")}
                data-testid="template-card-ai"
                className="text-left p-4 rounded-xl border-2 border-dashed border-[hsl(var(--halo-teal))] hover:bg-[hsl(var(--halo-teal)/0.05)] transition-colors"
              >
                <Sparkles className="w-6 h-6 text-[hsl(var(--halo-teal))]" strokeWidth={1.75} />
                <div className="mt-2 font-semibold text-sm">Draft with AI</div>
                <div className="text-xs text-muted-foreground mt-1">Describe your project in plain language — Claude will invent stages, tasks & folders.</div>
              </button>
              <button
                onClick={() => openConfigure({ slug: "__blank__", name: "Blank project", stages: [], tasks: [], folders: [] })}
                data-testid="template-card-blank"
                className="text-left p-4 rounded-xl border border-dashed border-border hover:border-foreground/40 transition-colors"
              >
                <Folder className="w-6 h-6 text-muted-foreground" strokeWidth={1.75} />
                <div className="mt-2 font-semibold text-sm">Blank project</div>
                <div className="text-xs text-muted-foreground mt-1">Just a title & deadline — no template scaffolding.</div>
              </button>
            </div>
          </div>
        )}

        {step === "ai" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Describe your project</Label>
              <Textarea
                rows={5}
                data-testid="ai-template-brief"
                placeholder="e.g. A 6-week podcast production for a beauty brand: two hosts, 8 episodes, launch coincides with a product drop, mix of recorded interviews and in-studio shows."
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              Claude will draft stages, tasks and folder blueprints tailored to your brief. You can edit everything on the next step.
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setStep("pick")}>← Back</Button>
              <Button data-testid="ai-generate-btn" onClick={runAI} disabled={!aiBrief.trim() || aiBusy}>
                {aiBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Draft template</>}
              </Button>
            </div>
          </div>
        )}

        {step === "configure" && chosen && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Project title</Label>
                <Input
                  data-testid="new-project-title"
                  autoFocus
                  value={form.project_title}
                  onChange={(e) => setForm({ ...form, project_title: e.target.value })}
                  placeholder={chosen.name === "Blank project" ? "Untitled project" : `New ${chosen.name}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Deadline (optional)</Label>
                <Input
                  type="date"
                  data-testid="new-project-deadline"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                />
              </div>
            </div>

            {chosen.slug !== "__blank__" && (
              <Tabs defaultValue="stages">
                <TabsList>
                  <TabsTrigger value="stages" data-testid="apply-tab-stages">
                    Stages ({(chosen.stages || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="tasks" data-testid="apply-tab-tasks">
                    Tasks ({(chosen.tasks || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="folders" data-testid="apply-tab-folders">
                    Folders ({(chosen.folders || []).length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="stages" className="space-y-2 mt-3">
                  {(chosen.stages || []).sort((a, b) => a.order - b.order).map((s) => (
                    <label key={s.key} className={cn("flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      included.stages[s.key] ? "border-[hsl(var(--studio-primary)/0.3)] bg-[hsl(var(--studio-primary)/0.03)]" : "border-border")}>
                      <input
                        type="checkbox"
                        className="mt-0.5 w-4 h-4 accent-[hsl(var(--studio-primary))]"
                        checked={!!included.stages[s.key]}
                        onChange={(e) => setIncluded((x) => ({ ...x, stages: { ...x.stages, [s.key]: e.target.checked } }))}
                        data-testid={`stage-include-${s.key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{s.name}</div>
                        {s.description && <div className="text-xs text-muted-foreground mt-0.5">{s.description}</div>}
                        {s.target_duration_days && <div className="text-[11px] text-muted-foreground mt-1">~{s.target_duration_days} days</div>}
                      </div>
                    </label>
                  ))}
                </TabsContent>

                <TabsContent value="tasks" className="space-y-1 mt-3 max-h-72 overflow-y-auto">
                  {(chosen.tasks || []).map((t) => (
                    <label key={t.title} className="flex items-start gap-3 p-2 rounded-md hover:bg-accent/40 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 accent-[hsl(var(--studio-primary))]"
                        checked={!!included.tasks[t.title]}
                        onChange={(e) => setIncluded((x) => ({ ...x, tasks: { ...x.tasks, [t.title]: e.target.checked } }))}
                        data-testid={`task-include-${t.title.slice(0, 20)}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{t.title}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {t.stage_key} · {t.priority} · day +{t.offset_days}
                        </div>
                      </div>
                    </label>
                  ))}
                </TabsContent>

                <TabsContent value="folders" className="space-y-2 mt-3">
                  {(chosen.folders || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((fl) => (
                    <label key={fl.key} className={cn("flex items-center gap-3 p-2.5 rounded-md border cursor-pointer",
                      included.folders[fl.key] ? "border-[hsl(var(--studio-primary)/0.3)] bg-[hsl(var(--studio-primary)/0.03)]" : "border-border")}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[hsl(var(--studio-primary))]"
                        checked={!!included.folders[fl.key]}
                        onChange={(e) => setIncluded((x) => ({ ...x, folders: { ...x.folders, [fl.key]: e.target.checked } }))}
                        data-testid={`folder-include-${fl.key}`}
                      />
                      <Folder className="w-4 h-4 text-muted-foreground" />
                      <div className="text-sm">{fl.name}</div>
                    </label>
                  ))}
                </TabsContent>
              </Tabs>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "configure" && (
            <>
              <Button variant="outline" onClick={() => setStep("pick")} data-testid="apply-back-btn">← Back</Button>
              <Button data-testid="apply-template-btn" onClick={apply} disabled={applying || !form.project_title.trim()}>
                {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><Check className="w-4 h-4 mr-2" /> Create project</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
